/**
 * FlexFreight control system (Oct 18 refinement). One implementation per control, used portal-wide:
 *  - TextField / TextAreaField: integrated (floating) labels inside a fixed-height box; real <label for>.
 *  - Select: base-ui listbox styled as FlexFreight (never the OS menu); "field" (floating label) and
 *    "toolbar" (compact, label inline) variants.
 *  - SearchInput: toolbar search with icon and clear.
 *  - FilterToolbar: one row, priority-ordered filters; whatever doesn't fit moves into "More filters".
 *  - StatusFilter: top statuses inline as a segmented group, every status inside More filters.
 *  - ConfirmDialog + useConfirm: branded replacement for window.confirm.
 * Styles: packages/ui/controls.css (tokens only). Motion is gated by prefers-reduced-motion there.
 */
import { AlertDialog } from "@base-ui/react/alert-dialog";
import { Dialog } from "@base-ui/react/dialog";
import { Popover } from "@base-ui/react/popover";
import { Select as BaseSelect } from "@base-ui/react/select";
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(" ");
const useIso = typeof window === "undefined" ? useEffect : useLayoutEffect;

/**
 * A controlled value that shows the user's pick on the very next frame. Filter pages write filters through the
 * URL inside a transition, so the control used to keep its old value until the whole table re-rendered (measured
 * 40-200ms in a production build, 150-450ms in dev): the segment didn't move and the select kept the old label.
 * The pick is shown only until the `value` prop moves (to anything); if the parent ignores the change, it reverts
 * after a second. `onChange` still runs synchronously, exactly as before (no transition wrapping, so focus moves
 * and dialogs a handler opens keep their timing).
 */
function useInstantValue<T>(value: T, same: (a: T, b: T) => boolean = Object.is): [T, (next: T, apply: () => void) => void] {
  const [pick, setPick] = useState<{ next: T; base: T } | null>(null);
  if (pick && !same(pick.base, value)) setPick(null); // the parent caught up (or moved elsewhere): show the prop
  useEffect(() => {
    if (!pick) return undefined;
    const t = setTimeout(() => setPick(null), 1000);
    return () => clearTimeout(t);
  }, [pick]);
  const commit = (next: T, apply: () => void) => {
    setPick({ next, base: value });
    apply();
  };
  return [pick ? pick.next : value, commit];
}
const sameList = <V,>(a: ReadonlyArray<V>, b: ReadonlyArray<V>) => a.length === b.length && a.every((x, i) => x === b[i]);

/* ------------------------------------------------------------------ icons */

function Chevron() {
  return (
    <svg className="ffc-chev" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4.5 6.25 8 9.75l3.5-3.5" />
    </svg>
  );
}
function Check() {
  return (
    <svg className="ffc-check" viewBox="0 0 16 16" aria-hidden="true">
      <path d="m3.5 8.5 3 3 6-7" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg className="ffc-ico" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="7" cy="7" r="4.25" />
      <path d="m10.25 10.25 3 3" />
    </svg>
  );
}
function XIcon() {
  return (
    <svg className="ffc-ico" viewBox="0 0 16 16" aria-hidden="true">
      <path d="m4.5 4.5 7 7m0-7-7 7" />
    </svg>
  );
}
function FilterIcon() {
  return (
    <svg className="ffc-ico" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M2.5 4h11M4.75 8h6.5M7 12h2" />
    </svg>
  );
}

/* ---------------------------------------------------------- field message */

/**
 * Hint or error under a field. The error line is a persistent polite live region (empty until there is an
 * error, taken out of the layout while empty), so a submit that reveals errors is announced, not just shown.
 */
function FieldMessage({ id, error, hint }: { id: string; error?: ReactNode; hint?: ReactNode }) {
  return (
    <>
      <p className="ffc-msg ffc-msg--error" id={`${id}-err`} aria-live="polite">
        {error || null}
      </p>
      {!error && hint ? (
        <p className="ffc-msg" id={`${id}-hint`}>
          {hint}
        </p>
      ) : null}
    </>
  );
}

/* ------------------------------------------------- focus first invalid */

/**
 * After any form submit that leaves fields invalid, move focus to the first `aria-invalid="true"` control in
 * that form, so its error (linked by aria-describedby) is read with it instead of several polite live regions
 * firing at once while focus stays on the submit button (WCAG 3.3.1 / 2.4.3). Install once at the app root;
 * returns the cleanup. Leaves focus alone when the submit moved it somewhere else (a heading, a dialog).
 */
export function focusFirstInvalidOnSubmit(doc: Document = document): () => void {
  const onSubmit = (ev: Event) => {
    const form = ev.target;
    if (!(form instanceof HTMLFormElement)) return;
    const before = doc.activeElement;
    requestAnimationFrame(() => {
      const active = doc.activeElement;
      // The page already moved focus on purpose (next step's heading, a dialog, a field it chose): respect it.
      if (active && active !== before && active !== doc.body) return;
      form.querySelector<HTMLElement>('[aria-invalid="true"]:not([disabled])')?.focus();
    });
  };
  doc.addEventListener("submit", onSubmit);
  return () => doc.removeEventListener("submit", onSubmit);
}

/* ------------------------------------------------------------- text field */

export type TextFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "placeholder" | "size"> & {
  /** Visible, accessible name. Sits inside the box; floats up when focused or filled. */
  label: string;
  /** Unit or short affix shown at the end of the box (lb, in, ft³, $). Announced via aria-describedby. */
  suffix?: ReactNode;
  /** Leading affix ($). */
  prefix?: ReactNode;
  /** One short line under the field. Use sparingly: only for information that prevents mistakes. */
  hint?: ReactNode;
  error?: ReactNode;
  /** Shows "Optional" after the label. Required is the default and is not decorated. */
  optional?: boolean;
  /** Example value shown only while focused and empty (never instead of the label). */
  example?: string;
  className?: string;
  /** Hide the label visually (still announced). For dense table-like rows. */
  hideLabel?: boolean;
  /** Height on the one field scale: "md" (default, form) or "sm" (dense: dialogs, side panels, table rows). */
  size?: "sm" | "md";
};

/** Text input with an integrated floating label. Fixed height (--ff-field-h); label never shifts layout. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, suffix, prefix, hint, error, optional, example, className, id, hideLabel, size = "md", ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? `f${auto.replace(/:/g, "")}`;
  const desc = [suffix ? `${inputId}-sfx` : null, error ? `${inputId}-err` : hint ? `${inputId}-hint` : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className={cx("ffc-field", size === "sm" ? "ffc-field--sm" : null, error ? "is-invalid" : null, rest.disabled ? "is-disabled" : null, hideLabel ? "is-bare" : null, className)}>
      <div className="ffc-box">
        {prefix ? <span className="ffc-affix ffc-affix--pre" aria-hidden="true">{prefix}</span> : null}
        <input
          {...rest}
          ref={ref}
          id={inputId}
          className="ffc-input"
          placeholder={hideLabel ? label : " "}
          aria-invalid={error ? true : rest["aria-invalid"]}
          aria-describedby={[desc, rest["aria-describedby"]].filter(Boolean).join(" ") || undefined}
        />
        <label htmlFor={inputId} className={cx("ffc-label", hideLabel ? "ffc-vh" : null)}>
          {label}
          {optional ? <span className="ffc-optional"> Optional</span> : null}
        </label>
        {example ? <span className="ffc-example" aria-hidden="true">{example}</span> : null}
        {suffix ? (
          <span className="ffc-affix" id={`${inputId}-sfx`}>
            {suffix}
          </span>
        ) : null}
      </div>
      <FieldMessage id={inputId} error={error} hint={hint} />
    </div>
  );
});

export type TextAreaFieldProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "placeholder"> & {
  label: string;
  /** Example text shown only while focused and empty (never instead of the label). */
  example?: string;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  className?: string;
};

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(function TextAreaField(
  { label, hint, error, optional, className, id, rows = 3, example, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? `t${auto.replace(/:/g, "")}`;
  return (
    <div className={cx("ffc-field ffc-field--area", error ? "is-invalid" : null, className)}>
      <div className="ffc-box">
        <textarea
          {...rest}
          ref={ref}
          id={inputId}
          rows={rows}
          className="ffc-input"
          placeholder=" "
          aria-invalid={error ? true : rest["aria-invalid"]}
          aria-describedby={[error ? `${inputId}-err` : hint ? `${inputId}-hint` : null, rest["aria-describedby"]].filter(Boolean).join(" ") || undefined}
        />
        <label htmlFor={inputId} className="ffc-label">
          {label}
          {optional ? <span className="ffc-optional"> Optional</span> : null}
        </label>
        {example ? <span className="ffc-example" aria-hidden="true">{example}</span> : null}
      </div>
      <FieldMessage id={inputId} error={error} hint={hint} />
    </div>
  );
});

/* ------------------------------------------------------------------ select */

export type SelectOption<V extends string = string> = {
  value: V;
  label: string;
  /** Right-aligned secondary text in the menu (a count, a code). */
  meta?: ReactNode;
  /** Leading visual in the menu (status dot, carrier logo, initials). Decorative: hidden from assistive tech, so the label must say it all. */
  icon?: ReactNode;
  disabled?: boolean;
  /**
   * An action row ("Save current view…", "Manage views"): quieter style with a leading icon, never shown as
   * the selected value. Picking it calls onChange(value) like any option; the page performs the action and
   * keeps its own value.
   */
  action?: boolean;
};
/** A hairline between groups of options (e.g. views above, actions below). */
export type SelectSeparator = { type: "separator"; key?: string };
export type SelectEntry<V extends string = string> = SelectOption<V> | SelectSeparator;
const isSep = (o: SelectEntry<string>): o is SelectSeparator => (o as SelectSeparator).type === "separator";

export type SelectProps<V extends string = string> = {
  label: string;
  /** Options, plus optional `{ type: "separator" }` entries and `action: true` rows. */
  options: ReadonlyArray<SelectEntry<V>>;
  value: V | null;
  onChange: (value: V) => void;
  /** "field": floating label, form height. "toolbar": compact, "Label: value" inline. */
  variant?: "field" | "toolbar";
  placeholder?: string;
  name?: string;
  /** Autofill token for the hidden form input (e.g. "country"), so browser autofill can set the value. */
  autoComplete?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  optional?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  className?: string;
  /** Toolbar variant: highlight as an active filter when the value isn't the default. */
  active?: boolean;
  /** Toolbar variant: hide "Label:" prefix (e.g. when the value already says it). */
  bareValue?: boolean;
  /** Extra description ids (an external note); merged with the hint/error ids. */
  "aria-describedby"?: string;
  /** Field variant height: "md" (default) or "sm" (dense). Toolbar variant is always control height. */
  size?: "sm" | "md";
};

/**
 * FlexFreight select. base-ui listbox (keyboard, typeahead, Escape, outside press, viewport-aware
 * positioning, portal so it never clips) styled to the portal. Submits through a hidden input when `name` is set.
 */
export function Select<V extends string = string>({
  label,
  options,
  value: valueProp,
  onChange,
  variant = "field",
  placeholder = "Choose…",
  name,
  autoComplete,
  id,
  disabled,
  required,
  optional,
  hint,
  error,
  className,
  active,
  bareValue,
  "aria-describedby": describedBy,
  size = "md",
}: SelectProps<V>) {
  const auto = useId();
  const baseId = id ?? `s${auto.replace(/:/g, "")}`;
  const choices = useMemo(() => options.filter((o): o is SelectOption<V> => !isSep(o)), [options]);
  const [value, commit] = useInstantValue(valueProp);
  const current = choices.find((o) => o.value === value && !o.action) ?? null;
  const items = useMemo(() => choices.map((o) => ({ value: o.value, label: o.label })), [choices]);
  const toolbar = variant === "toolbar";
  return (
    <div
      className={cx(
        "ffc-field",
        toolbar ? "ffc-field--toolbar" : "ffc-field--select",
        !toolbar && size === "sm" ? "ffc-field--sm" : null,
        current ? "is-filled" : null,
        error ? "is-invalid" : null,
        disabled ? "is-disabled" : null,
        toolbar && active ? "is-active" : null,
        className,
      )}
    >
      <BaseSelect.Root<V>
        items={items}
        value={value}
        onValueChange={(v) => {
          if (v === null) return;
          // Action rows ("Save view…") never become the value: no optimistic label for them.
          if (choices.find((o) => o.value === v)?.action) onChange(v as V);
          else commit(v as V, () => onChange(v as V));
        }}
        name={name}
        autoComplete={autoComplete}
        disabled={disabled}
        required={required}
        modal={false}
      >
        <div className="ffc-box">
          <BaseSelect.Trigger
            id={baseId}
            className="ffc-trigger"
            aria-label={toolbar ? `${label}: ${current?.label ?? placeholder}` : undefined}
            aria-describedby={[error ? `${baseId}-err` : hint ? `${baseId}-hint` : null, describedBy].filter(Boolean).join(" ") || undefined}
            aria-invalid={error ? true : undefined}
          >
            {toolbar ? (
              <span className="ffc-trigger__text">
                {bareValue ? null : <span className="ffc-trigger__label">{label}</span>}
                <span className="ffc-trigger__value">{current?.label ?? placeholder}</span>
              </span>
            ) : (
              <span className="ffc-trigger__value" title={current?.label}>
                {current ? (
                  <>
                    {current.icon ? <span className="ffc-opt__icon" aria-hidden="true">{current.icon}</span> : null}
                    {current.label}
                  </>
                ) : (
                  <span className="ffc-placeholder">{placeholder}</span>
                )}
              </span>
            )}
            <BaseSelect.Icon className="ffc-trigger__icon">
              <Chevron />
            </BaseSelect.Icon>
          </BaseSelect.Trigger>
          {toolbar ? null : (
            <BaseSelect.Label className="ffc-label">
              {label}
              {optional ? <span className="ffc-optional"> Optional</span> : null}
            </BaseSelect.Label>
          )}
        </div>
        <BaseSelect.Portal>
          <BaseSelect.Positioner className="ffc-positioner" sideOffset={6} alignItemWithTrigger={false} collisionPadding={12}>
            <BaseSelect.Popup className="ffc-menu">
              <BaseSelect.List className="ffc-menu__list">
                {options.map((o, i) =>
                  isSep(o) ? (
                    <BaseSelect.Separator key={o.key ?? `sep-${i}`} className="ffc-sep" />
                  ) : (
                    <BaseSelect.Item key={o.value} value={o.value} disabled={o.disabled} className={cx("ffc-opt", o.action ? "ffc-opt--action" : null)}>
                      {o.icon ? <span className="ffc-opt__icon" aria-hidden="true">{o.icon}</span> : null}
                      <BaseSelect.ItemText className="ffc-opt__text">{o.label}</BaseSelect.ItemText>
                      {o.meta !== undefined ? <span className="ffc-opt__meta">{o.meta}</span> : null}
                      {o.action ? null : (
                        <BaseSelect.ItemIndicator className="ffc-opt__check">
                          <Check />
                        </BaseSelect.ItemIndicator>
                      )}
                    </BaseSelect.Item>
                  ),
                )}
              </BaseSelect.List>
            </BaseSelect.Popup>
          </BaseSelect.Positioner>
        </BaseSelect.Portal>
      </BaseSelect.Root>
      <FieldMessage id={baseId} error={error} hint={hint} />
    </div>
  );
}

/* ------------------------------------------------------------- time field */

/** "8:30 AM" from "08:30"; the raw string when it doesn't parse. */
export function timeLabel(hhmm: string): string {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  if (!m) return hhmm;
  const h = Number(m[1]);
  return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
}
/** "HH:MM" slots every `step` minutes from `from` to `to` (inclusive), plus `extra` when it's off the grid. */
export function timeSlots(step = 30, from = "00:00", to = "23:59", extra?: string | null): string[] {
  const mins = (t: string) => { const [h, m] = t.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
  const out: string[] = [];
  for (let t = mins(from); t <= mins(to); t += step) out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
  if (extra && /^\d{2}:\d{2}$/.test(extra) && !out.includes(extra)) out.push(extra);
  return out.sort();
}
export type TimeFieldProps = Omit<SelectProps<string>, "options" | "value" | "onChange"> & {
  /** "HH:MM" (24h), exactly what the API takes. */
  value: string;
  onChange: (hhmm: string) => void;
  /** Minutes between choices (default 30). A value off the grid is kept as its own choice. */
  step?: number;
  from?: string;
  to?: string;
  /** A first choice meaning "no time" (value ""), e.g. "Carrier default". Without it, "" shows the placeholder. */
  emptyLabel?: string;
};
const NO_TIME = "__none";
/** Branded time picker (never the OS clock): the shared `Select` with time slots, labelled "8:30 AM". */
export function TimeField({ value, onChange, step = 30, from, to, emptyLabel, ...rest }: TimeFieldProps) {
  const options = useMemo(
    () => [
      ...(emptyLabel ? [{ value: NO_TIME, label: emptyLabel }] : []),
      ...timeSlots(step, from, to, value).map((t) => ({ value: t, label: timeLabel(t) })),
    ],
    [step, from, to, value, emptyLabel],
  );
  return <Select<string> {...rest} options={options} value={value || (emptyLabel ? NO_TIME : null)} onChange={(v) => onChange(v === NO_TIME ? "" : v)} />;
}

/* ------------------------------------------------------------ search input */

export type SearchInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "size"> & {
  label: string;
  value: string;
  onChange: (v: string) => void;
  /** "toolbar" (default, --ff-control-h) or "field" (--ff-field-h, to sit in a form row with TextFields). */
  size?: "toolbar" | "field";
};
/** Toolbar search. Extra input attributes (maxLength, aria-describedby, name, inputMode…) pass through. */
export function SearchInput({ label, value, onChange, placeholder, className, id, autoFocus, onKeyDown, size = "toolbar", ...rest }: SearchInputProps) {
  const auto = useId();
  const inputId = id ?? `q${auto.replace(/:/g, "")}`;
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div className={cx("ffc-search", size === "field" ? "ffc-search--field" : null, value ? "is-filled" : null, className)}>
      <label htmlFor={inputId} className="ffc-vh">
        {label}
      </label>
      <SearchIcon />
      <input
        autoComplete="off"
        spellCheck={false}
        {...rest}
        ref={ref}
        id={inputId}
        type="search"
        className="ffc-search__input"
        placeholder={placeholder ?? label}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.defaultPrevented) return;
          if (e.key === "Escape" && value) {
            e.preventDefault();
            onChange("");
          }
        }}
      />
      {value ? (
        <button
          type="button"
          className="ffc-search__clear"
          aria-label="Clear search"
          onClick={() => {
            onChange("");
            ref.current?.focus();
          }}
        >
          <XIcon />
        </button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ toggle */

export type ToggleProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size" | "onChange"> & {
  /** Visible label (the whole control is the hit target). */
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** "check" (default): a control-height chip with a checkbox. "switch": a sliding switch, for settings. */
  look?: "check" | "switch";
  /** Secondary line under the label (switch look in settings lists). */
  description?: ReactNode;
  className?: string;
};

/**
 * Checkbox toggle in the control system: a real <input type="checkbox"> (keyboard, forms, getByLabelText)
 * inside a control-height chip, thin focus ring, lime only when on. One look for Stackable, Hazmat,
 * "Schedule pickup", "Guaranteed only" and settings notifications (`look="switch"`).
 */
export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { label, checked, onChange, look = "check", description, className, disabled, ...rest },
  ref,
) {
  return (
    <label className={cx("ffc-toggle", look === "switch" ? "ffc-toggle--switch" : null, checked ? "is-on" : null, disabled ? "is-disabled" : null, className)}>
      <input {...rest} ref={ref} type="checkbox" role={look === "switch" ? "switch" : undefined} checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="ffc-toggle__mark" aria-hidden="true">
        {look === "switch" ? <span className="ffc-toggle__knob" /> : <Check />}
      </span>
      <span className="ffc-toggle__text">
        <span className="ffc-toggle__label">{label}</span>
        {description ? <span className="ffc-toggle__desc">{description}</span> : null}
      </span>
    </label>
  );
});

/* --------------------------------------------------------------- file field */

export type FileFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "size"> & {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  optional?: boolean;
  /** Button text (defaults to "Choose file"). */
  buttonLabel?: string;
  /** Shown while nothing is chosen. */
  emptyText?: string;
  className?: string;
};

/**
 * File picker in the field system: label inside the box, the chosen file name as the value, a "Choose file"
 * button at the end. The real <input type="file"> covers the box, so the native OS picker, drag-and-drop onto
 * the field, keyboard and getByLabelText all keep working. A caller that clears the input in code
 * (`input.value = ""` after an upload) is picked up on its next render, so the shown name never goes stale.
 */
export const FileField = forwardRef<HTMLInputElement, FileFieldProps>(function FileField(
  { label, hint, error, optional, buttonLabel = "Choose file", emptyText = "No file chosen", className, id, onChange, multiple, ...rest },
  ref,
) {
  const auto = useId();
  const inputId = id ?? `u${auto.replace(/:/g, "")}`;
  const [names, setNames] = useState<string[]>([]);
  const own = useRef<HTMLInputElement | null>(null);
  const setRef = useCallback(
    (el: HTMLInputElement | null) => {
      own.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );
  useIso(() => {
    const now = Array.from(own.current?.files ?? []).map((f) => f.name);
    setNames((prev) => (prev.length === now.length && prev.every((n, i) => n === now[i]) ? prev : now));
  });
  return (
    <div className={cx("ffc-field ffc-field--file", names.length ? "is-filled" : null, error ? "is-invalid" : null, rest.disabled ? "is-disabled" : null, className)}>
      <div className="ffc-box">
        <input
          {...rest}
          ref={setRef}
          id={inputId}
          type="file"
          multiple={multiple}
          className="ffc-file__input"
          aria-invalid={error ? true : undefined}
          aria-describedby={[error ? `${inputId}-err` : hint ? `${inputId}-hint` : null, rest["aria-describedby"]].filter(Boolean).join(" ") || undefined}
          onChange={(e) => {
            setNames(Array.from(e.target.files ?? []).map((f) => f.name));
            onChange?.(e);
          }}
        />
        <label htmlFor={inputId} className="ffc-label ffc-file__label">
          {label}
          {optional ? <span className="ffc-optional"> Optional</span> : null}
        </label>
        <span className="ffc-file__value" aria-hidden="true">
          {names.length ? (names.length > 1 ? `${names.length} files` : names[0]) : <span className="ffc-placeholder">{emptyText}</span>}
        </span>
        <span className="ffc-file__btn" aria-hidden="true">{buttonLabel}</span>
      </div>
      <FieldMessage id={inputId} error={error} hint={hint} />
    </div>
  );
});

/* ----------------------------------------------------------- filter toolbar */

export type FilterItem = {
  key: string;
  /** Lower shows first; the toolbar keeps adding items inline in this order while they fit. */
  priority: number;
  /** Compact control for the toolbar row. */
  inline: ReactNode;
  /** Labelled control for the More filters panel (defaults to `inline`; `null` = no copy in the panel). */
  panel?: ReactNode | null;
  /** Panel group heading (defaults to none). */
  group?: string;
  /** Is this filter narrowing the results right now? Counted on "More filters" when hidden. */
  active?: boolean;
  /**
   * Keep the `panel` copy in More filters even while the inline control fits. For inline controls that show
   * a subset (StatusFilter with `primary`): every option stays reachable at every width.
   */
  alwaysInPanel?: boolean;
};

/** Pure fit calculation (exported for tests): which items stay inline for a given width. */
export function fitFilters(
  items: ReadonlyArray<{ key: string; priority: number; width: number }>,
  available: number,
  gap: number,
): Set<string> {
  const keep = new Set<string>();
  let used = 0;
  for (const it of [...items].sort((a, b) => a.priority - b.priority)) {
    const need = it.width + (keep.size > 0 || used > 0 ? gap : 0);
    if (used + need > available) break; // priority order: never skip ahead to a smaller, less important filter
    used += need;
    keep.add(it.key);
  }
  return keep;
}

/**
 * The one media-query hook (FilterToolbar and `usePhone` in components.tsx both use it). SSR/jsdom-safe; re-syncs
 * in the effect so a change between first render and subscription is not missed.
 */
export function useMediaQuery(query: string): boolean {
  const [m, setM] = useState(() => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(query);
    const on = () => setM(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [query]);
  return m;
}
const usePhone = () => useMediaQuery("(max-width: 40rem)");

/**
 * One-row filter toolbar. Search grows; filters are added inline by priority while they fit; the rest
 * (plus `extra`) live in "More filters", whose badge counts active filters inside it. Never wraps.
 */
export function FilterToolbar({
  label = "Filters",
  search,
  items,
  extra,
  trailing: trailingProp,
  onReset,
  anyActive,
  className,
  searchMin = 15,
  meta,
  views,
  showReset = false,
}: {
  label?: string;
  search?: ReactNode;
  items: ReadonlyArray<FilterItem>;
  /** Filters that always live in the panel (rarely used). */
  extra?: ReadonlyArray<Omit<FilterItem, "inline" | "priority"> & { panel: ReactNode }>;
  /** Right-aligned controls that belong to the filter system (saved views, density). Never move. */
  trailing?: ReactNode;
  onReset?: () => void;
  anyActive?: boolean;
  className?: string;
  /** Minimum search width in rem before filters start moving into the panel. */
  searchMin?: number;
  /** Read-only status at the end of the row ("42 invoices"). Not a filter: never moves into the panel, stays on phones. */
  meta?: ReactNode;
  /** Saved views: a "View" select at the trailing edge (never moves), with an optional "Save current view…" row. */
  views?: SavedViewsConfig;
  /** Also show a quiet "Reset" in the row while any filter is active (it always lives in More filters). */
  showReset?: boolean;
}) {
  const viewsNode = views ? <SavedViews {...views} /> : null;
  const trailing = viewsNode || trailingProp ? (
    <>
      {viewsNode}
      {trailingProp}
    </>
  ) : null;
  const resetRef = useRef<HTMLButtonElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);
  const trailRef = useRef<HTMLDivElement>(null);
  const metaRef = useRef<HTMLDivElement>(null);
  const phone = usePhone();
  const [inlineKeys, setInlineKeys] = useState<Set<string>>(() => new Set(items.map((i) => i.key)));
  // Widths are measured on the LIVE inline controls (no hidden duplicate row, so no duplicate ids/labels) and
  // cached per key, so an item moved into More filters keeps its last width for the next fit. Items/trailing
  // are new objects on every parent render: read through refs; the fit re-runs on item-key/priority change,
  // phone switch, any size change of the row, an item, More filters or trailing (ResizeObserver), and fonts.
  const resetShown = showReset && !!onReset && !!anyActive;
  const sig = `${items.map((i) => `${i.key}:${i.priority}`).join("|")}#${search ? 1 : 0}${trailing ? 1 : 0}${meta ? 1 : 0}${resetShown ? 1 : 0}#${phone ? 1 : 0}`;
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const widths = useRef(new Map<string, number>());
  const hasSearch = !!search;
  const hasTrailing = !!trailing;
  /** Something always lives in More filters, so its button never disappears. */
  const pinnedPanel = (extra?.length ?? 0) > 0 || items.some((i) => i.alwaysInPanel && i.panel !== null);

  const recompute = useCallback(() => {
    const row = rowRef.current;
    if (!row) return;
    if (phone) {
      setInlineKeys((prev) => (prev.size === 0 ? prev : new Set()));
      return;
    }
    const total = row.getBoundingClientRect().width;
    if (total === 0) {
      // Not laid out (jsdom/hidden): everything inline, including items added after the first render.
      const all = new Set(itemsRef.current.map((it) => it.key));
      setInlineKeys((prev) => (prev.size === all.size && [...all].every((k) => prev.has(k)) ? prev : all));
      return;
    }
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const gap = parseFloat(getComputedStyle(row).columnGap) || 8;
    const all = itemsRef.current;
    const unknown = all.filter((it) => !widths.current.has(it.key)).map((it) => it.key);
    const known = all.filter((it) => widths.current.has(it.key)).map((it) => ({ key: it.key, priority: it.priority, width: widths.current.get(it.key)! }));
    // Each present box costs its width plus one gap; a hidden (display: none) More filters costs nothing.
    const box = (el: HTMLElement | null) => {
      const w = el?.getBoundingClientRect().width ?? 0;
      return w > 0 ? w + gap : 0;
    };
    const fixed = (hasSearch ? searchMin * rem + gap : 0) + (hasTrailing ? box(trailRef.current) : 0) + box(metaRef.current) + box(resetRef.current);
    let next = fitFilters(known, Math.max(0, total - fixed - box(moreRef.current)), gap);
    // When everything fitting inline would empty More filters (and hide its button), test that case without the
    // button's width too; otherwise a filter stays stuck in the panel after widening by less than the button.
    if (!pinnedPanel && next.size < known.length) {
      const allIn = fitFilters(known, Math.max(0, total - fixed), gap);
      if (allIn.size === known.length) next = allIn;
    }
    for (const k of unknown) next.add(k); // render once so it gets measured; the observer re-fits right after
    setInlineKeys((prev) => (prev.size === next.size && [...next].every((k) => prev.has(k)) ? prev : next));
  }, [phone, hasSearch, hasTrailing, searchMin, pinnedPanel]);

  const roRef = useRef<ResizeObserver | null>(null);
  const rafRef = useRef(0);
  const schedule = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(recompute);
  }, [recompute]);
  useIso(() => {
    recompute();
  }, [recompute, sig]);
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) {
        const k = (e.target as HTMLElement).dataset.fkey;
        if (k) widths.current.set(k, e.target.getBoundingClientRect().width);
      }
      schedule();
    });
    roRef.current = ro;
    for (const el of [rowRef.current, moreRef.current, trailRef.current, metaRef.current, resetRef.current]) if (el) ro.observe(el);
    rowRef.current?.querySelectorAll<HTMLElement>("[data-fkey]").forEach((el) => ro.observe(el));
    let alive = true;
    document.fonts?.ready.then(() => { if (alive) recompute(); });
    return () => { alive = false; cancelAnimationFrame(rafRef.current); ro.disconnect(); roRef.current = null; };
  }, [recompute, schedule, sig, phone]);
  // Stable per-key ref callbacks: observe an item wrapper while it is mounted (re-creating them every render
  // would unobserve/observe each render and re-trigger the observer).
  const itemRefs = useRef(new Map<string, (el: HTMLDivElement | null) => void>());
  const itemRef = (k: string) => {
    let f = itemRefs.current.get(k);
    if (!f) {
      let cur: HTMLDivElement | null = null;
      f = (el) => {
        if (cur && roRef.current) roRef.current.unobserve(cur);
        cur = el;
        if (el && roRef.current) roRef.current.observe(el);
      };
      itemRefs.current.set(k, f);
    }
    return f;
  };

  const shownInline = (i: FilterItem) => !phone && (inlineKeys.has(i.key) || !widths.current.has(i.key));
  const panelItems = [
    ...(phone && trailing ? [{ key: "__trailing", group: undefined, active: false, panel: trailing }] : []),
    ...items
      .filter((i) => i.panel !== null && (!shownInline(i) || i.alwaysInPanel))
      .map((i) => ({ key: i.key, group: i.group, active: shownInline(i) ? false : i.active, panel: i.panel === undefined ? i.inline : i.panel })),
    ...(extra ?? []),
  ];
  const hiddenActive = panelItems.filter((i) => i.active).length;

  return (
    <div className={cx("ffc-toolbar", className)} role="toolbar" aria-label={label}>
      <div className="ffc-toolbar__row" ref={rowRef}>
        {search ? <div className="ffc-toolbar__search">{search}</div> : null}
        {items
          .filter(shownInline)
          .sort((a, b) => a.priority - b.priority)
          .map((i) => (
            <div key={i.key} ref={itemRef(i.key)} data-fkey={i.key} className="ffc-toolbar__item">
              {i.inline}
            </div>
          ))}
        <div ref={moreRef} className={cx("ffc-toolbar__more", panelItems.length === 0 ? "is-empty" : null)}>
          {panelItems.length > 0 ? (
            <MoreFilters phone={phone} count={hiddenActive} items={panelItems} onReset={onReset} anyActive={anyActive} />
          ) : null}
        </div>
        {resetShown ? (
          <button ref={resetRef} type="button" className="ffc-link ffc-toolbar__reset" onClick={onReset}>
            Reset
          </button>
        ) : null}
        {trailing && !phone ? (
          <div ref={trailRef} className="ffc-toolbar__trail">
            {trailing}
          </div>
        ) : null}
        {meta ? (
          <div ref={metaRef} className="ffc-toolbar__meta" role="status">
            {meta}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export type SavedViewsConfig = {
  label?: string;
  items: ReadonlyArray<{ id: string; label: string }>;
  /** The applied view (null = unsaved filters: shows "Custom"). */
  value: string | null;
  onChange: (id: string) => void;
  /** Offer "Save current view…" (the page asks for a name and stores it). */
  onSave?: () => void;
};
const SAVE_VIEW = "__save_view";
/** Saved views select for FilterToolbar: views above, the save action below a hairline. */
export function SavedViews({ label = "View", items, value, onChange, onSave }: SavedViewsConfig) {
  const options: SelectEntry<string>[] = [
    ...items.map((v) => ({ value: v.id, label: v.label })),
    ...(onSave ? [{ type: "separator" as const, key: "views-sep" }, { value: SAVE_VIEW, label: "Save current view…", action: true }] : []),
  ];
  return (
    <Select
      variant="toolbar"
      label={label}
      value={value}
      placeholder="Custom"
      options={options}
      onChange={(v) => (v === SAVE_VIEW ? onSave?.() : onChange(v))}
    />
  );
}

function MoreFilters({
  phone,
  count,
  items,
  onReset,
  anyActive,
}: {
  phone: boolean;
  count: number;
  items: ReadonlyArray<{ key: string; group?: string; active?: boolean; panel: ReactNode }>;
  onReset?: () => void;
  anyActive?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const trigger = (
    <>
      <FilterIcon />
      {/* On phones every filter lives in the sheet, so the button is just "Filters" (keeps the row on one line). */}
      {phone ? (
        <>
          <span className="ffc-vh">More filters</span>
          <span aria-hidden="true">Filters</span>
        </>
      ) : (
        <span>More filters</span>
      )}
      {count > 0 ? (
        <>
          {/* aria-label on a plain span is prohibited and dropped by some screen readers: real hidden text instead. */}
          <span className="ffc-count" aria-hidden="true">
            {count}
          </span>
          <span className="ffc-vh">, {count} active</span>
        </>
      ) : null}
    </>
  );
  const groups: { name: string | undefined; items: typeof items }[] = [];
  for (const it of items) {
    const g = groups.find((x) => x.name === it.group);
    if (g) (g.items as Array<(typeof items)[number]>).push(it);
    else groups.push({ name: it.group, items: [it] });
  }
  const body = (
    <>
      <div className="ffc-panel__head">
        <span className="ffc-panel__title">Filters</span>
        {onReset ? (
          // aria-disabled, not disabled: pressing Reset all from the keyboard turns it off, and a disabled button
          // drops focus to <body> (the panel stayed open with nothing focused). It stays focusable and inert.
          <button type="button" className="ffc-link" onClick={anyActive ? onReset : undefined} aria-disabled={!anyActive || undefined}>
            Reset all
          </button>
        ) : null}
      </div>
      <div className="ffc-panel__body">
        {groups.map((g, gi) => (
          <div key={g.name ?? `g${gi}`} className="ffc-panel__group">
            {g.name ? <p className="ffc-panel__group-title">{g.name}</p> : null}
            {g.items.map((i) => (
              <div key={i.key} className="ffc-panel__item">
                {i.panel}
              </div>
            ))}
          </div>
        ))}
      </div>
    </>
  );
  if (phone) {
    return (
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger className={cx("ffc-tbtn", count > 0 ? "is-active" : null)}>{trigger}</Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Backdrop className="ffc-scrim" />
          <Dialog.Popup className="ffc-sheet">
            <span className="ffc-sheet__grab" aria-hidden="true" />
            <Dialog.Title className="ffc-vh">Filters</Dialog.Title>
            {body}
            <div className="ffc-sheet__foot">
              <Dialog.Close className="ffc-tbtn ffc-tbtn--primary">Show results</Dialog.Close>
            </div>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger className={cx("ffc-tbtn", count > 0 ? "is-active" : null)}>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Positioner className="ffc-positioner" sideOffset={8} align="end" collisionPadding={12}>
          <Popover.Popup className="ffc-menu ffc-panel">
            <Popover.Title className="ffc-vh">More filters</Popover.Title>
            {body}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}

/* ----------------------------------------------------------- status filter */

export type StatusOption<V extends string = string> = {
  value: V;
  label: string;
  count?: number;
  /** Status tone for the dot: maps to the portal status colors. */
  tone?: "success" | "info" | "warning" | "danger" | "neutral";
};

const fmtCount = (n: number) => new Intl.NumberFormat("en-US").format(n);

type StatusFilterBase<V extends string> = {
  label?: string;
  options: ReadonlyArray<StatusOption<V>>;
  primary: ReadonlyArray<V>;
  mode: "inline" | "panel";
  /** Counts are still loading: reserve each missing count's width (a quiet placeholder) so the segments,
   *  and the FilterToolbar row measured from them, don't jump when the numbers arrive. */
  countsPending?: boolean;
};
export type StatusFilterProps<V extends string = string> =
  | (StatusFilterBase<V> & { multiple?: false; value: V; onChange: (v: V) => void })
  | (StatusFilterBase<V> & {
      /** Several statuses at once: `value` is the chosen set ([] = all). Panel = checkbox list. */
      multiple: true;
      value: ReadonlyArray<V>;
      onChange: (v: V[]) => void;
      /** The "everything" option (e.g. "all"); picking it clears the set. */
      allValue?: V;
    });

/**
 * Status filter. `inline` mode: a segmented group of the `primary` statuses (plus the current one if it
 * isn't primary). `panel` mode: every status as a radio list. Same value, same counts, same colors.
 * `multiple`: value is an array; the panel is a checkbox list; an inline segment reads pressed only when
 * exactly that one status is selected (the "all" segment when none is), and pressing it selects just it.
 */
export function StatusFilter<V extends string = string>(props: StatusFilterProps<V>) {
  const { label = "Status", options, primary, mode, countsPending = false } = props;
  const name = useId();
  const multi = props.multiple === true;
  // Shown selection updates on the next frame, before the (transition-driven) list re-render: see useInstantValue.
  const [chosen, commit] = useInstantValue<ReadonlyArray<V>>(props.multiple ? props.value : [props.value], sameList);
  const allValue = props.multiple ? props.allValue : undefined;
  const isOn = (v: V) => (multi ? (v === allValue ? chosen.length === 0 : chosen.includes(v)) : chosen[0] === v);
  const pressed = (v: V) => (multi ? (v === allValue ? chosen.length === 0 : chosen.length === 1 && chosen[0] === v) : chosen[0] === v);
  const emit = (next: V[]) =>
    commit(next, () => {
      if (props.multiple) props.onChange(next);
      else props.onChange(next[0]!);
    });
  const pickOnly = (v: V) => emit(props.multiple && v === allValue ? [] : [v]);
  const toggle = (v: V) => {
    if (!props.multiple) return emit([v]);
    if (v === allValue) return emit([]);
    emit(chosen.includes(v) ? chosen.filter((x) => x !== v) : [...chosen, v]);
  };
  if (mode === "panel") {
    return (
      <fieldset className="ffc-status-list">
        <legend className="ffc-panel__group-title">{label}</legend>
        {options.map((o) => (
          <label key={o.value} className={cx("ffc-status-opt", isOn(o.value) ? "is-on" : null, multi ? "is-check" : null)}>
            <input type={multi ? "checkbox" : "radio"} name={name} value={o.value} checked={isOn(o.value)} onChange={() => toggle(o.value)} />
            {multi ? <span className="ffc-status-opt__box" aria-hidden="true"><Check /></span> : null}
            <span className={cx("ffc-dot", o.tone ? `ffc-dot--${o.tone}` : null)} aria-hidden="true" />
            <span className="ffc-status-opt__label">{o.label}</span>
            {o.count !== undefined ? <span className="ffc-status-opt__count">{fmtCount(o.count)}</span> : countsPending ? <span className="ffc-status-opt__count ffc-count-pending" aria-hidden="true" /> : null}
          </label>
        ))}
      </fieldset>
    );
  }
  const shown = options.filter((o) => primary.includes(o.value) || (multi ? chosen.length === 1 && chosen[0] === o.value : o.value === chosen[0]));
  return (
    <div className="ffc-seg" role="group" aria-label={label}>
      {shown.map((o) => (
        <button key={o.value} type="button" className="ffc-seg__btn" aria-pressed={pressed(o.value)} onClick={() => pickOnly(o.value)}>
          {o.tone ? <span className={cx("ffc-dot", `ffc-dot--${o.tone}`)} aria-hidden="true" /> : null}
          <span>{o.label}</span>
          {o.count !== undefined ? <span className="ffc-seg__count">{fmtCount(o.count)}</span> : countsPending ? <span className="ffc-seg__count ffc-count-pending" aria-hidden="true" /> : null}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------- confirm dialog */

export type ConfirmOptions = {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Destructive styling for the confirm button. */
  danger?: boolean;
};

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

export type ConfirmDialogProps = ConfirmOptions & {
  open: boolean;
  /** Confirm pressed. Return a promise to keep the dialog open (button busy) until it settles. */
  onConfirm: () => void | Promise<unknown>;
  /** Cancel, Escape or outside press. */
  onCancel: () => void;
  /** Confirm button busy (for a parent that tracks its own mutation). */
  loading?: boolean;
};

/**
 * Branded confirmation (replaces window.confirm): base-ui AlertDialog, so focus is trapped, Escape and outside
 * press cancel, and focus returns to the opener. Destructive (`danger`): red confirm, focus starts on Cancel so
 * Enter never deletes by accident. Declarative; `useConfirm` is the promise form built on it.
 */
export function ConfirmDialog({ open, title, body, confirmLabel = "Confirm", cancelLabel = "Cancel", danger, onConfirm, onCancel, loading }: ConfirmDialogProps) {
  const confirmBtn = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  const working = !!loading || busy;
  return (
    <AlertDialog.Root open={open} onOpenChange={(o) => (!o && !working ? onCancel() : undefined)}>
      <AlertDialog.Portal>
        <AlertDialog.Backdrop className="ffc-scrim" />
        {/* Destructive: focus starts on Cancel (Enter never deletes by accident). Otherwise on the confirm button. */}
        <AlertDialog.Popup className="ffc-confirm" initialFocus={danger ? true : confirmBtn}>
          <AlertDialog.Title className="ffc-confirm__title">{title}</AlertDialog.Title>
          {body ? <AlertDialog.Description className="ffc-confirm__body">{body}</AlertDialog.Description> : null}
          <div className="ffc-confirm__actions">
            <AlertDialog.Close className="ffc-tbtn" onClick={() => onCancel()} disabled={working}>
              {cancelLabel}
            </AlertDialog.Close>
            <button
              ref={confirmBtn}
              type="button"
              className={cx("ffc-tbtn", danger ? "ffc-tbtn--danger" : "ffc-tbtn--primary")}
              aria-busy={working || undefined}
              disabled={working}
              onClick={() => {
                const r = onConfirm();
                if (r && typeof (r as Promise<unknown>).then === "function") {
                  setBusy(true);
                  void (r as Promise<unknown>).finally(() => setBusy(false));
                }
              }}
            >
              {confirmLabel}
              {working ? <span className="ffx-btn__spin" aria-hidden="true" /> : null}
            </button>
          </div>
        </AlertDialog.Popup>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

/**
 * Promise-based confirmation in the portal's style (replaces window.confirm). Render `dialog` once;
 * `confirm({...})` resolves true/false. Focus goes to Cancel for destructive actions, to the confirm button otherwise.
 */
export function useConfirm() {
  const [pending, setPending] = useState<Pending | null>(null);
  // What the dialog shows. Kept after closing so the title, body and button style stay put while the dialog
  // fades out (rendering from `pending` blanked the text and flipped a red Delete button to lime mid-exit).
  const [shown, setShown] = useState<ConfirmOptions | null>(null);
  // The open request's resolver: a second confirm() (or unmounting mid-question) answers the first with false,
  // so no awaiting handler is left hanging forever.
  const live = useRef<Pending["resolve"] | null>(null);
  const confirm = useCallback(
    (o: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        live.current?.(false);
        live.current = resolve;
        setPending({ ...o, resolve });
        setShown(o);
      }),
    [],
  );
  useEffect(() => () => live.current?.(false), []);
  const close = (ok: boolean) => {
    pending?.resolve(ok);
    if (live.current === pending?.resolve) live.current = null;
    setPending(null);
  };
  const dialog = (
    <ConfirmDialog
      open={pending !== null}
      title={shown?.title ?? ""}
      body={shown?.body}
      confirmLabel={shown?.confirmLabel}
      cancelLabel={shown?.cancelLabel}
      danger={shown?.danger}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );
  return { confirm, dialog };
}

/* ---------------- InfoTip ---------------- */

/**
 * A small (i) beside a heading that holds its description, so headers stay one line. Hover (after a short
 * intent delay) or click/tap opens it; Escape or clicking away closes it. The text stays in the DOM, visually
 * hidden, for screen readers and search, so the button only adds a visual way in.
 */
export function InfoTip({ children, label = "More info", className }: { children: ReactNode; label?: string; className?: string }) {
  const id = useId();
  return (
    <span className={cx("ffc-info", className)}>
      <span id={id} className="ffc-vh">
        {children}
      </span>
      <Popover.Root>
        <Popover.Trigger className="ffc-info__btn" aria-label={label} aria-describedby={id} openOnHover delay={250} closeDelay={120}>
          <svg viewBox="0 0 16 16" width="1em" height="1em" aria-hidden="true" focusable="false">
            <circle cx="8" cy="8" r="6.6" fill="none" stroke="currentColor" strokeWidth="1.4" />
            <path d="M8 7.1v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="8" cy="4.9" r="0.95" fill="currentColor" />
          </svg>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Positioner className="ffc-positioner" side="bottom" align="start" sideOffset={6} collisionPadding={12}>
            <Popover.Popup className="ffc-menu ffc-info__pop">
              {children}
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </span>
  );
}
